import { FieldType } from '@indigohive/cogfy-types'
import { Select } from '../../Select'
import { ChatMiddlewareConditionSource } from '../ChatMiddlewareConditionForm'
import { GetFieldPropertiesResult } from '@indigohive/cogfy-types/endpoints/getFieldProperties'
import { useCallback, useMemo } from 'react'

export type ChatMiddlewareConditionSourceSelectProps = {
  fields?: {
    id: string
    name: string
    type: FieldType
  }[]
  fieldProperties?: GetFieldPropertiesResult
  value: ChatMiddlewareConditionSource
  onChange: (source: ChatMiddlewareConditionSource) => void
}

export function ChatMiddlewareConditionSourceSelect (props: ChatMiddlewareConditionSourceSelectProps) {
  const { fields = [], fieldProperties, onChange } = props

  const sourceType = props.value.type

  const fieldOptions = useMemo(() =>
    fields.flatMap(field =>
      fieldProperties?.data?.[field.type]?.map(property => ({
        value: `${field.id}:${property.name}`,
        label: property.name === 'value' ? field.name : `${field.name}: ${property.name}`
      })) ?? []
    ), [fields, fieldProperties]
  )

  const value = useCallback(() => {
    if (sourceType === 'field') {
      return props.value.field.id
    }

    if (sourceType === 'template') {
      return `${props.value.field.id}:${props.value.field.property}`
    }

    return sourceType
  }, [props.value])

  return (
    <>
      {(sourceType !== 'field') && (
        <Select
          size="md"
          value={value()}
          options={[
            { label: 'Message: content type', value: 'content_type' },
            { label: 'Message: content', value: 'content' },
            ...fieldOptions
          ]}
          onChange={event => {
            if (event.target.value === 'content_type' || event.target.value === 'content') {
              onChange({ type: event.target.value as 'content' | 'content_type' })
            } else {
              const split = event.target.value.split(':')
              const fieldId = split[0]
              const property = split[1] ?? 'value'
              const field = fields.find(field => field.id === fieldId)

              if (field) {
                onChange({ type: 'template', field: { id: field.id, type: field.type, property } })
              }
            }
          }}
        />
      )}

      {/* Maintain backward compatibility */}
      {sourceType === 'field' && (
        <Select
          size="md"
          value={value()}
          options={[
            { label: 'Content type', value: 'content_type' },
            { label: 'Content', value: 'content' },
            ...fields?.map(field => ({
              label: `Field: ${field.name}`,
              value: field.id
            }))
          ]}
          onChange={event => {
            const field = fields.find(field => field.id === event.target.value)

            if (field) {
              onChange({ type: 'field', field: { id: field.id, type: field.type } })
            } else {
              onChange({ type: event.target.value as 'content' | 'content_type' })
            }
          }}
        />
      )}
    </>
  )
}
